import { Checkbox, SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { fetchActivatableMarketingPromos } from 'actions/providers'
import { useModalToast } from 'components/misc/Toaster'
import { useAppDispatch } from 'hooks/hooks'
import { useEffect, useMemo, useState } from 'react'

interface LeadsOffer {
    value?: number
    type?: string
    promo_length?: number
}

interface MarketingPromoOption {
    promo_id: number
    name: string
    identifier: string
    description?: string | null
    eligible: boolean
    offers_info?: Array<Record<string, LeadsOffer>> | null
}

interface AddActiveMarketingPromoModalProps {
    providerId: number
    providerName?: string
    canOverride?: boolean
    onConfirm: (data: { promoId: number; override: boolean }) => Promise<void>
    onCancel: () => void
}

const getOffer = (
    offersInfo: MarketingPromoOption['offers_info'],
    key: 'recurring-leads' | 'one-time-leads',
): LeadsOffer | null => {
    if (!Array.isArray(offersInfo)) {
        return null
    }
    for (const offerObj of offersInfo) {
        if (offerObj && typeof offerObj === 'object' && offerObj[key]) {
            return offerObj[key]
        }
    }
    return null
}

const formatPromoLabel = (promo: MarketingPromoOption): string => {
    const recurring = getOffer(promo.offers_info, 'recurring-leads')
    if (recurring?.value !== undefined && recurring?.value !== null) {
        const duration = recurring.promo_length
            ? ` for ${recurring.promo_length} month${recurring.promo_length === 1 ? '' : 's'}`
            : ''
        return `${promo.name} — ${recurring.value} lead${
            recurring.value === 1 ? '' : 's'
        }/month${duration}`
    }

    const oneTime = getOffer(promo.offers_info, 'one-time-leads')
    if (oneTime?.value !== undefined && oneTime?.value !== null) {
        return `${promo.name} — ${oneTime.value} one-time lead${oneTime.value === 1 ? '' : 's'}`
    }

    return promo.name
}

const AddActiveMarketingPromoModal = NiceModal.create(
    ({
        providerId,
        providerName,
        canOverride = false,
        onConfirm,
        onCancel,
    }: AddActiveMarketingPromoModalProps) => {
        const modal = useModal()
        const dispatch = useAppDispatch()
        const [promos, setPromos] = useState<MarketingPromoOption[]>([])
        const [isLoading, setIsLoading] = useState(true)
        const [selectedPromoId, setSelectedPromoId] = useState<number | null>(null)
        const [override, setOverride] = useState(false)
        const [isSubmitting, setIsSubmitting] = useState(false)
        const { addModalToast } = useModalToast()

        useEffect(() => {
            const loadPromos = async () => {
                try {
                    setIsLoading(true)
                    const fetchedPromos: MarketingPromoOption[] = await dispatch(
                        fetchActivatableMarketingPromos(providerId),
                    )
                    setPromos(fetchedPromos)
                    if (fetchedPromos.length > 0) {
                        setSelectedPromoId(fetchedPromos[0].promo_id)
                    }
                } catch (error) {
                    addModalToast({
                        id: 'load-error',
                        msg:
                            error instanceof Error
                                ? error.message
                                : 'Failed to load marketing promos',
                        icon: 'error',
                        red: true,
                    })
                } finally {
                    setIsLoading(false)
                }
            }
            loadPromos()
        }, [dispatch, providerId])

        // Reset the override confirmation whenever the selected promo changes so it
        // has to be re-acknowledged per promo.
        useEffect(() => {
            setOverride(false)
        }, [selectedPromoId])

        const selectedPromo = useMemo(
            () => promos.find((promo) => promo.promo_id === selectedPromoId) || null,
            [promos, selectedPromoId],
        )
        const needsOverride = selectedPromo ? !selectedPromo.eligible : false
        const canSubmit =
            !!selectedPromoId &&
            !isLoading &&
            !isSubmitting &&
            (!needsOverride || (canOverride && override))

        const handlePromoChange = (event: SelectSimpleChangeEvent) => {
            setSelectedPromoId(Number(event.target.value))
        }

        const handleConfirm = async () => {
            if (!selectedPromoId) {
                return
            }
            setIsSubmitting(true)
            try {
                await onConfirm({ promoId: selectedPromoId, override: needsOverride })
                modal.resolve()
                modal.hide()
            } catch (error) {
                addModalToast({
                    id: 'save-error',
                    msg:
                        error instanceof Error
                            ? error.message
                            : 'Failed to add active marketing promo',
                    icon: 'error',
                    red: true,
                })
            } finally {
                setIsSubmitting(false)
            }
        }

        const handleCancel = () => {
            onCancel()
            modal.resolve()
            modal.hide()
        }

        return (
            <BaseModal onClose={handleCancel}>
                <BaseModalHeader>
                    <h2 className="text-display-xs font-bold">Add active marketing promo</h2>
                </BaseModalHeader>
                <BaseModalMainContent>
                    <div className="space-y-4">
                        <p className="text-md text-neutral-600">
                            Assign and immediately activate a marketing promo for{' '}
                            {providerName || 'this provider'}. This grants the promo's free leads
                            right away (and, for recurring promos, keeps them coming every month).
                        </p>
                        <div>
                            <label className="block text-md font-bold text-neutral-700 mb-1">
                                Promo *
                            </label>
                            {isLoading ? (
                                <div className="text-md text-neutral-500">Loading promos...</div>
                            ) : promos.length === 0 ? (
                                <div className="text-md text-red-500">
                                    No marketing promos available for this country
                                </div>
                            ) : (
                                <SelectSimple
                                    value={selectedPromoId?.toString() || ''}
                                    onChange={handlePromoChange}
                                    disabled={isSubmitting}
                                    options={promos.map((promo) => ({
                                        value: promo.promo_id.toString(),
                                        name: `${formatPromoLabel(promo)}${
                                            promo.eligible ? '' : ' (not eligible)'
                                        }`,
                                    }))}
                                />
                            )}
                        </div>
                        {selectedPromo && needsOverride && canOverride && (
                            <div className="rounded border border-orange-300 bg-orange-50 p-3 space-y-2">
                                <p className="text-md text-orange-800">
                                    <strong>{selectedPromo.name}</strong> — this provider does not
                                    currently meet the eligibility rules for this promo. You can
                                    override the check to grant or restore it anyway (e.g. to
                                    reactivate an existing promo).
                                </p>
                                <Checkbox
                                    name="override_eligibility"
                                    title="Override eligibility and grant anyway"
                                    value={override}
                                    onChange={() => setOverride(!override)}
                                />
                            </div>
                        )}
                        {selectedPromo && needsOverride && !canOverride && (
                            <p className="text-md text-red-600">
                                <strong>{selectedPromo.name}</strong> — this provider is not
                                eligible for this promo. Only an admin can override the eligibility
                                check, so please ask an admin to grant it.
                            </p>
                        )}
                        {selectedPromo && !needsOverride && (
                            <p className="text-md text-green-700">
                                Provider is eligible for this promo.
                            </p>
                        )}
                    </div>
                </BaseModalMainContent>
                <BaseModalFooter className="flex justify-end gap-2">
                    <Button appearance="secondary" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button appearance="primary" onClick={handleConfirm} disabled={!canSubmit}>
                        {isSubmitting ? 'Adding...' : 'Add active marketing promo'}
                    </Button>
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default AddActiveMarketingPromoModal
